/**
 * AuthGate — /attendance のラッパ。 認証状態に応じて画面を切替える。
 *
 *   loading           : 読込中スピナー
 *   config_error      : 環境変数 VITE_GOOGLE_CLIENT_ID 未設定 / GIS 読み込み失敗
 *   signed_out        : Google ログインボタン
 *   unregistered      : 自己登録ダイアログ (display_name 入力 → master.register)
 *   pending_approval  : 承認待ち画面
 *   ready             : 子要素 (AttendancePage) を描画
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps): JSX.Element {
  const { status, user, errorMessage } = useAuth();

  switch (status) {
    case 'loading':
      return (
        <main className="page auth-page">
          <span className="loading-inline" role="status">
            <span className="loading-spinner" aria-hidden="true" />
            読み込み中…
          </span>
        </main>
      );
    case 'config_error':
      return (
        <main className="page auth-page">
          <h1>設定が未完了です</h1>
          <p className="error-box" role="alert">
            {errorMessage ??
              'Google ログインの設定が読み込めませんでした。 管理者にお伝えください。'}
          </p>
        </main>
      );
    case 'signed_out':
      return <SignedOutScreen />;
    case 'unregistered':
      return <SelfRegisterScreen />;
    case 'pending_approval':
      return <PendingApprovalScreen userName={user?.display_name ?? ''} />;
    case 'ready':
      return <>{children}</>;
    default:
      return <></>;
  }
}

function SignedOutScreen(): JSX.Element {
  const { renderSignInButton, errorMessage } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      renderSignInButton(buttonRef.current);
    }
  }, [renderSignInButton]);

  return (
    <main className="page auth-page">
      <h1>出勤管理</h1>
      <p>
        灯文舎メンバー専用ページです。 お持ちの Google アカウントでログインしてください。
      </p>
      <div ref={buttonRef} className="auth-button-host" />
      {errorMessage ? (
        <p className="error-box" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </main>
  );
}

function SelfRegisterScreen(): JSX.Element {
  const { user, registerMaster, signOut } = useAuth();
  const [name, setName] = useState<string>(() => user?.name ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('お名前を入れてください');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await registerMaster(trimmed);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(`登録に失敗しました: ${msg}`);
      setSubmitting(false);
    }
  };

  return (
    <main className="page auth-page">
      <h1>はじめての利用ですね</h1>
      <p>
        {user?.email
          ? `${user.email} をこのページに登録します。`
          : 'Google アカウントをこのページに登録します。'}
        登録後、 管理者の承認をお待ちください。
      </p>
      <label className="period-picker">
        お名前 (社内で表示される名前)
        <input
          type="text"
          value={name}
          placeholder="例: 寺元一耕"
          onChange={(e) => setName(e.target.value)}
          disabled={submitting}
        />
      </label>
      {error ? (
        <p className="error-box" role="alert">
          {error}
        </p>
      ) : null}
      <div className="attendance-actions">
        <button
          type="button"
          className="primary-button"
          onClick={handleSubmit}
          disabled={submitting || !name.trim()}
        >
          {submitting ? '登録中…' : '登録する'}
        </button>
        <button type="button" className="link-button" onClick={signOut}>
          別のアカウントでログインし直す
        </button>
      </div>
    </main>
  );
}

interface PendingApprovalScreenProps {
  userName: string;
}

function PendingApprovalScreen({ userName }: PendingApprovalScreenProps): JSX.Element {
  const { refreshWhoami, signOut } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  return (
    <main className="page auth-page">
      <h1>承認待ちです</h1>
      <p>
        {userName ? `${userName} さん、 ` : ''}
        登録ありがとうございます。 管理者の承認が完了すると、 出勤管理を利用できるようになります。
      </p>
      <p className="page-subtitle">
        承認されたら、 下のボタンで状態を更新してください。
      </p>
      <div className="attendance-actions">
        <button
          type="button"
          className="primary-button"
          disabled={refreshing}
          onClick={async () => {
            setRefreshing(true);
            try {
              await refreshWhoami();
            } finally {
              setRefreshing(false);
            }
          }}
        >
          {refreshing ? '確認中…' : '承認状況を確認する'}
        </button>
        <button type="button" className="link-button" onClick={signOut}>
          ログアウト
        </button>
      </div>
    </main>
  );
}
